import React, { useMemo, useState, useEffect } from "react";
import Switch from "@/components/common/Switch/Switch";
import ButtonWithDropdownOption from "@/components/common/ButtonWithDropdownOption/Button";
import AngleArrow from "@assets/icons/common/angle_down.svg?react";
import Tooltip from "@/components/common/Tooltip/Tooltip";
import "./StatTesting.css";
import { STAT_TESTING_OPTIONS } from "./StatTesting.constant";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES_CHILDREN_LOOKUP } from "@/routes/routes";
import type { ChildRoute } from "@/routes/routes.types";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { dequal } from "dequal";
import { updateUrlParams } from "@/utils/url";
import classNames from "classnames";

interface StatTestingProps {}

function getDaysDifference(startDate: any, endDate: any) {
  if (!startDate || !endDate) {
    return 0;
  }
  const start = new Date(startDate);
  const end = new Date(endDate);
  const differenceInTime = end.getTime() - start.getTime();
  const differenceInDays = differenceInTime / (1000 * 3600 * 24);
  return differenceInDays;
}

function StatTesting({}: StatTestingProps) {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const { mode, period, startDate, endDate, statTestingEnabled } = useSelector(
    (state: RootState) => ({
      statTestingEnabled: state.userSelection.statTest || "NONE",
      mode: state.userSelection.mode,
      period: state.userSelection.period,
      startDate: state.userSelection.periodStartDate,
      endDate: state.userSelection.periodEndDate,
    }),
    (prev, next) => dequal(prev, next)
  );

  async function handleSelect(selectedValue: string) {
    updateUrlParams(
      {
        statTest: selectedValue,
      },
      navigate
    );
  }

  const currentRouteDetail: ChildRoute = useMemo(
    () => ROUTES_CHILDREN_LOOKUP[location.pathname] || {},
    [location.pathname]
  );

  const { statTestingDetails } = currentRouteDetail;

  useEffect(() => {
    const isPtpDisabled =
      statTestingDetails?.PTP?.hidden ||
      statTestingDetails?.PTP?.enabled === false;
    if (statTestingEnabled === "PTP" && isPtpDisabled) {
      handleSelect("POP");
    }
  }, [statTestingDetails, statTestingEnabled]);

  return (
    <div className="stat-testing-wrapper">
      <ButtonWithDropdownOption
        className="stat-testing-popup-container"
        isDisabled={
          !(
            statTestingEnabled === "POP" ||
            statTestingEnabled === "YOY" ||
            statTestingEnabled === "PTP"
          )
        }
        trigger={
          <div
            className={classNames(
              "text-text-500 text-xs cursor-pointer flex gap-1 items-center bg-white px-2 py-1"
            )}
          >
            <Switch
              checked={
                statTestingEnabled === "POP" ||
                statTestingEnabled === "YOY" ||
                statTestingEnabled === "PTP"
              }
              handleChange={(val: boolean) =>
                handleSelect(
                  statTestingEnabled === "POP" ||
                    statTestingEnabled === "YOY" ||
                    statTestingEnabled === "PTP"
                    ? "NONE"
                    : "POP"
                )
              }
            />
            <span className="pl-1 font-medium">
              Stat Testing{" "}
              {statTestingEnabled === "POP"
                ? `(Period-P)`
                : statTestingEnabled === "YOY"
                ? `(Year-Y)`
                : statTestingEnabled === "PTP"
                ? "(Peer-P)"
                : ""}
            </span>
            {(statTestingEnabled === "POP" ||
              statTestingEnabled === "YOY" ||
              statTestingEnabled === "PTP") && (
              <AngleArrow
                height={12}
                className={
                  true
                    ? "stat-testing-arrow stat-testing-arrow-open relative ml-[-4px]"
                    : "stat-testing-arrow  ml-[-4px]"
                }
              />
            )}
          </div>
        }
        onShow={() => {
          setIsTooltipVisible(true);
        }}
        onHide={() => {
          setIsTooltipVisible(false);
        }}
      >
        <ul className="stat-testing-popup">
          {STAT_TESTING_OPTIONS?.filter(
            (option: any) => !statTestingDetails?.[option?.id]?.hidden
          )?.map((option: any) => {
            if (option.id === "YOY") {
              const daysDifference = getDaysDifference(startDate, endDate);
              if (daysDifference > 365) {
                return (
                  <Tooltip
                    contentList={[
                      "Year-over-Year comparison is only available for Months & Quarters of one year or less.",
                    ]}
                    placement="left"
                    classname="stat-testing-disabled-message"
                    offset={[0, 20]}
                  >
                    <li
                      key={option.id}
                      className={"stat-testing-disabled-option"}
                    >
                      <div className="mt-1">{option.icon}</div>
                      <div>
                        <div className="stat-testing-title">
                          {option.title}
                        </div>
                        <div className="stat-testing-description">
                          {option.description}
                        </div>
                      </div>
                    </li>
                  </Tooltip>
                );
              }
              if (period === "P_W") {
                return (
                  <Tooltip
                    contentList={[
                      "Year-over-Year comparison is only available for Months & Quarters.",
                    ]}
                    placement="left"
                    classname="stat-testing-disabled-message"
                    offset={[0, 20]}
                  >
                    <li
                      key={option.id}
                      className={"stat-testing-disabled-option"}
                    >
                      <div className="mt-1">{option.icon}</div>
                      <div>
                        <div className="stat-testing-title">
                          {option.title}
                        </div>
                        <div className="stat-testing-description">
                          {option.description}
                        </div>
                      </div>
                    </li>
                  </Tooltip>
                );
              }
            }
            if (statTestingDetails?.[option?.id]?.enabled === false) {
              return (
                <Tooltip
                  contentList={[
                    statTestingDetails?.[option?.id]?.disabledMessage,
                  ]}
                  placement="left"
                  classname="stat-testing-disabled-message"
                  offset={[0, 20]}
                >
                  <li
                    key={option.id}
                    className={"stat-testing-disabled-option"}
                  >
                    <div className="mt-1">{option.icon}</div>
                    <div>
                      <div className="stat-testing-title">{option.title}</div>
                      <div className="stat-testing-description">
                        {option.description}
                      </div>
                    </div>
                  </li>
                </Tooltip>
              );
            }
            if (statTestingDetails?.[option?.id]?.enabled === true) {
              if (
                statTestingDetails?.[option?.id]?.disabledModes?.includes(mode)
              ) {
                return (
                  <Tooltip
                    contentList={[
                      statTestingDetails?.[option?.id]?.disabledModesMessages[
                        mode
                      ],
                    ]}
                    placement="left"
                    classname="stat-testing-disabled-message"
                    offset={[0, 20]}
                  >
                    <li
                      key={option.id}
                      className={"stat-testing-disabled-option"}
                    >
                      <div className="mt-1">{option.icon}</div>
                      <div>
                        <div className="stat-testing-title">{option.title}</div>
                        <div className="stat-testing-description">
                          {option.description}
                        </div>
                      </div>
                    </li>
                  </Tooltip>
                );
              }
            }
            return (
              <li
                key={option.id}
                className={
                  option.id === statTestingEnabled ? "active-stat-testing" : ""
                }
                onClick={() => handleSelect(option.id)}
              >
                <div className="mt-1">{option.icon}</div>
                <div>
                  <div className="stat-testing-title">{option.title}</div>
                  <div className="stat-testing-description">
                    {option.description}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </ButtonWithDropdownOption>
    </div>
  );
}

export default StatTesting;
